import { getFruit } from '../data/fruits';
import type { FruitBehavior, FruitKind } from './types';

export interface FruitBehaviorTiming {
  readonly idleTicks: number;
  readonly warningTicks: number;
  readonly travelTicks: number;
  readonly recoveryTicks: number;
}

export const FRUIT_BEHAVIOR_TIMING = {
  stationary: { idleTicks: Number.MAX_SAFE_INTEGER, warningTicks: 0, travelTicks: 0, recoveryTicks: 0 },
  // Strawberries never stop: no idle pause and no per-step telegraph, just a
  // slow continuous glide that the renderer interpolates tile to tile.
  'slow-roam': { idleTicks: 0, warningTicks: 0, travelTicks: 96, recoveryTicks: 0 },
  'leaf-flight': { idleTicks: 240, warningTicks: 45, travelTicks: 90, recoveryTicks: 18 },
  teleport: { idleTicks: 600, warningTicks: 45, travelTicks: 30, recoveryTicks: 0 },
  runner: { idleTicks: 45, warningTicks: 12, travelTicks: 18, recoveryTicks: 0 },
} as const satisfies Readonly<Record<FruitBehavior, FruitBehaviorTiming>>;

/** Warning, travel, and recovery phases all consume one readability slot. */
export const FRUIT_BEHAVIOR_CONCURRENCY_CAP = {
  stationary: 0,
  'slow-roam': 24,
  'leaf-flight': 3,
  teleport: 2,
  runner: 4,
} as const satisfies Readonly<Record<FruitBehavior, number>>;

export const FRUIT_ACTION_RETRY_TICKS = 12;

/** Fruit families not listed here intentionally remain stationary. */
export function fruitBehaviorFor(kind: FruitKind): FruitBehavior {
  return getFruit(kind).behavior;
}

export function initialFruitActionDelay(behavior: FruitBehavior, actorIndex: number): number {
  switch (behavior) {
    case 'slow-roam': return actorIndex % 8 * 7;
    case 'leaf-flight': return 180 + actorIndex % 8 * 24;
    case 'teleport': return 600 + actorIndex % 6 * 30;
    case 'runner': return 36 + actorIndex % 6 * 8;
    default: return Number.MAX_SAFE_INTEGER;
  }
}
